import React, { useEffect, useState, Suspense } from 'react';
import { createClient } from '@supabase/supabase-js';
import CookieBanner from './CookieBanner'; 
import ErrorBoundary from './ErrorBoundary';

// Lazy Loading των components για ταχύτητα
const Menu = React.lazy(() => import('./Menu'));
const Dashboard = React.lazy(() => import('./Dashboard'));
const SuperAdmin = React.lazy(() => import('./SuperAdmin'));
const LandingPage = React.lazy(() => import('./LandingPage'));
const WaiterPOS = React.lazy(() => import('./WaiterPOS'));
const PrivacyPolicy = React.lazy(() => import('./PrivacyPolicy'));
const Terms = React.lazy(() => import('./Terms'));

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

const FullScreenLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-900">
    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin shadow-[0_0_15px_rgba(59,130,246,0.5)]"></div>
  </div>
);

function App() {
  const currentPath = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  const storeId = params.get('store');
  
  // ΒΕΛΤΙΩΜΕΝΟ ROUTING: Πιάνει και το /?admin και το /admin (χωρίς ερωτηματικό)
  const isAdmin = params.has('admin') || currentPath.includes('admin'); 
  const isBoss = params.has('boss') || currentPath.includes('boss');
  const isPos = params.has('pos') || currentPath.includes('pos'); 

  const isLegalPage = currentPath === '/privacy' || currentPath === '/terms';

  // --- PWA SMART ROUTER ---
  useEffect(() => {
    // Αν προσπαθείς να μπεις σε admin/pos/νομικά, ΑΚΥΡΩΝΟΥΜΕ το "έξυπνο" redirect του PWA
    if (isAdmin || isBoss || isPos || isLegalPage) return;

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

    // ΔΙΟΡΘΩΣΗ: Αποθηκεύουμε ΜΟΝΟ το storeId, όχι το table του πελάτη!
    if (storeId) {
      localStorage.setItem('servato_pwa_route', `?store=${storeId}`);
    } 
    else if (isStandalone) {
      const savedRoute = localStorage.getItem('servato_pwa_route');
      if (savedRoute && window.location.search !== savedRoute) {
        window.location.replace(window.location.origin + '/' + savedRoute);
      }
    }
  }, [isAdmin, isBoss, isPos, isLegalPage, storeId]);

  const [isActive, setIsActive] = useState(true);
  const [isPosEnabled, setIsPosEnabled] = useState(true);
  
  // Το loading χρειάζεται ΜΟΝΟ αν είναι απλός πελάτης (Menu)
  const isCustomerMenu = !!storeId && !isAdmin && !isBoss && !isPos && !isLegalPage;
  const [isLoading, setIsLoading] = useState(isCustomerMenu); 

  useEffect(() => {
    // Τραβάμε δεδομένα ΜΟΝΟ αν είμαστε πελάτης (γιατί Admin & POS τα τραβάνε μόνα τους)
    if (!isCustomerMenu) return; 

    if (storeId === "4" && isAdmin) {
      localStorage.removeItem(`sb-${process.env.REACT_APP_SUPABASE_URL?.split('//')[1]?.split('.')[0]}-auth-token`);
    }

    const checkStoreStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('stores')
          .select('is_active, enable_waiter_pos')
          .eq('id', storeId)
          .single();

        if (error) throw error;
        
        if (data) {
          if (data.is_active === false) setIsActive(false);
          if (data.enable_waiter_pos === false) setIsPosEnabled(false);
        }
      } catch (error) {
        console.error("Σφάλμα ελέγχου κατάστασης:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // 1. Αρχικός έλεγχος κατάστασης
    checkStoreStatus();

    // 2. REALTIME ΠΑΡΑΚΟΛΟΥΘΗΣΗ: Αν ο Admin κλειδώσει το μαγαζί, το κινητό ενημερώνεται ΖΩΝΤΑΝΑ!
    const storeSubscription = supabase
      .channel(`public:stores:id=eq.${storeId}`)
      .on(
        'postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'stores', filter: `id=eq.${storeId}` },
        (payload) => {
          console.log("Realtime Store Update Received:", payload.new);
          if (payload.new) {
            setIsActive(payload.new.is_active !== false);
            setIsPosEnabled(payload.new.enable_waiter_pos !== false);
          }
        }
      )
      .subscribe();

    // Cleanup function
    return () => {
      supabase.removeChannel(storeSubscription);
    };
  }, [storeId, isCustomerMenu, isAdmin]);

  let content = null;

  // ROUTING ΛΟΓΙΚΗ (Με απόλυτη προτεραιότητα στα νομικά κείμενα)
  if (currentPath === '/privacy') {
    content = <PrivacyPolicy />;
  } else if (currentPath === '/terms') {
    content = <Terms />;
  } else if (isBoss) {
    content = <SuperAdmin />;
  } else if (isAdmin) { 
    content = <Dashboard />;
  } else if (isPos) {
    content = <WaiterPOS />; 
  } else if (isLoading) {
    content = <FullScreenLoader />;
  } else if (!isActive && isCustomerMenu) {
    content = (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-4 text-center font-sans">
        <div className="text-7xl mb-6">🔒</div>
        <h1 className="text-3xl font-black text-white uppercase tracking-widest mb-3">Προσωρινα Μη Διαθεσιμο</h1>
        <p className="text-gray-400 font-bold">Η πρόσβαση στο κατάστημα έχει ανασταλεί.</p>
      </div>
    );
  } else if (storeId) {
    content = <Menu />;
  } else {
    content = <LandingPage />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<FullScreenLoader />}>
        {content}
        {/* Εμφάνιση του Banner παντού ΕΚΤΟΣ από τις σελίδες /privacy και /terms */}
        {!isLegalPage && <CookieBanner />}
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
